import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// Redux
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getPosition, usePositionReducer, resetReducerPosition } from "@/main/store/position";

// Types
import { KeyValue } from "@/main/models/common";
import { PartCatalogRequest, PositionRequest } from "@/main/models/request";
import { PartCatalogResponse } from "@/main/models/response/partCatalogResponse";

// Api
import { partCatalogApi } from "@/main/api";

// Services
import { getDefaultOptionDrop, getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { VehicleAutoCompleteResponse, XnuupPartIdAutoCompleteResponse } from "@/main/models/response";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";

// Components
import Box from "@/main/components/shared/box";
import ContainerScreen from "@/main/components/shared/containerScreen/containerLayout";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import { AreaButton } from "@/main/components/shared/styles";
import Button from "@/main/components/shared/button/button";
import VehicleAutoCompleteComponent from "@/main/components/shared/vehicle/VehicleAutoCompleteComponent";
import XnuupPartIdAutoCompleteComponent from "@/main/components/shared/xnuupPartIdAutoComplete/XnuupPartIdAutoCompleteComponent";

// Components/Styles Local
import ModalPartsCatalogDetails from "./components/modalPartsCatalogDetails/modalPartsCatalogDetails";
import PartsCatalogGrid from "./components/partsCatalogGrid/partsCatalogGrid";

import * as S from "./styles";
import { withQueryparams } from "@/main/components/core/queryParams/queryparams";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

const INITIAL_FORM_STATE = {
	country: null as KeyValue | null,
	partNumber: "" as string | null,
	description: "" as string | null,
	part: "" as string, //isGuid string
	position: null as KeyValue | null,
	vehicleSubModel: "" as string, //isGuid string
	vehicleManufacturer: "" as string, //isGuid string
	vehicleModel: "" as string, //isGuid string
	vehicle: "" as string,
	partName: "" as string,
};

export type IPartsCatalogForm = typeof INITIAL_FORM_STATE;

const INITIAL_MODAL_DETAILS_STATE = {
	opanModal: false as boolean,
	guid: null as string | null,
};

export type IModalDetails = typeof INITIAL_MODAL_DETAILS_STATE;

export const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};

export interface IGridData {
	data: PartCatalogResponse[];
	totalItems: number;
	isLoading: boolean;
}

function PartsCatalogScreen() {
	const dispatch = useDispatch();

	const countryReducer = useCountryReducer();
	const positionReducer = usePositionReducer();

	const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.countriesUser.data);

	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [modalDetails, setModalDetails] = useState<IModalDetails>(INITIAL_MODAL_DETAILS_STATE);
	const [hasClear, setHasClear] = useState<boolean>(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [formFilters, setFormFilters] = useState<PartCatalogRequest | null>(null);
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [showFilters, setShowFilters] = useState<boolean>(true);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);

	const {
		control,
		customHandleSubmit,
		setValue,
		formState: { errors },
		reset,
		watch,
	} = useFormQueryParams<IPartsCatalogForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
		mapDefaultValues: {
			country: countryReducer.countriesUser,

			position: positionReducer.position,
		},
		onSubmit: onSubmitSearch,
	});

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}

	const partWatcher = watch("part");

	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);

	function onSubmitSearch(dataForm: IPartsCatalogForm) {
		setPageIndex(PaginationEnum.PageIndexIntial);
		setLoadingSave(true);

		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});

		const request: PartCatalogRequest = removeEmptyValues({
			guidCountry: String(dataForm.country?.key) || "",
			guidPosition: String(dataForm.position?.key) || "",
			partNumber: dataForm.partNumber || "",
			description: dataForm.description || "",
			guidPart: dataForm.part || undefined,
			vehicleSubModel: dataForm.vehicleSubModel,
			vehicleManufacturer: dataForm.vehicleManufacturer,
			vehicleModel: dataForm.vehicleModel,
			pageIndex: PaginationEnum.PageIndexIntial,
			pageSize: PaginationEnum.PageSize30,
		});

		setFormFilters(request);

		partCatalogApi
			.searchgrid(request)
			.then((res) => {
				setGridData({
					isLoading: false,
					totalItems: res.data.totalNumberOfRecords,
					data: res.data.totalNumberOfRecords > 0 ? res.data.results : [],
				});
				setHasShowMore(res.data.results.length < res.data.totalNumberOfRecords);
			})
			.catch(() => {})
			.finally(() => {
				setLoadingSave(false);
			});
		setShowFilters(!showFilters);
	}

	function handleClickClear() {
		reset(INITIAL_FORM_STATE);
		setFormFilters(null);
		setHasClear(true);
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
		setIsLoadingInfiniteScroll(false);
		if (optionDefaultCountryDrop) setValue("country", optionDefaultCountryDrop);

		setGridData(INITIAL_GRID_STATE);
	}

	function loadOptionsByXnuupPartId(partId: XnuupPartIdAutoCompleteResponse) {
		if (partId) {
			setValue("partName", partId.partPIESName);
			return setValue("part", partId.partGuid);
		}
		setValue("partName", "");
		setValue("part", "");
	}

	useEffect(() => {
		dispatch(getCountriesUser());

		return () => {
			dispatch(resetReducerPosition());
		};
	}, []);

	useEffect(() => {
		if (partWatcher) {
			const request: PositionRequest = {
				partGuid: String(partWatcher),
			};
			dispatch(getPosition(request));
		} else {
			dispatch(resetReducerPosition());
			setValue("position", null);
		}
	}, [partWatcher]);

	// CONSTANTS LAYOUT
	const titleScreen = `${getTranslateByKey(
		"information.registration",
	)} > ${getTranslateByKey("information.partsCatalog")}`;

	return (
		<>
			<ContainerScreen hasBackground title={titleScreen}>
				<AreaButton>
					<Button onClick={handleFilters} id="id-partsCatalog-button-filters">
						{getTranslateByKey("information.filters")}
					</Button>
				</AreaButton>
				<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
					<form onSubmit={customHandleSubmit} noValidate>
						<S.AreaForm>
							<KogAutoCompleteControlled
								id="id-partsCatalog-autoComplete-country"
								nameCustom="country"
								label={getTranslateByKey("information.country")}
								options={countryReducer.countriesUser.data}
								isLoading={countryReducer.countriesUser.isLoading}
								rulesCustom={{ required: true }}
								errorMessageCustom={
									errors.country?.type === "required" ? getTranslateByKey("information.countryRequired", true) : ""
								}
								setValueCustom={setValue}
								disabled={countryReducer.countriesUser.data == null || optionDefaultCountryDrop != null}
								control={control}
								onChangeCustom={() => {}}
								isDefaultValueUnique
							/>

							<KogInputTextControlledNew
								id="id-partsCatalog-txt-partnumber"
								name="partNumber"
								label={getTranslateByKey("information.partnumber")}
								control={control}
								controllerProps={{ rules: { required: false } }}
							/>

							<KogInputTextControlledNew
								id="id-partsCatalog-txt-description"
								name="description"
								label={getTranslateByKey("information.description")}
								control={control}
								controllerProps={{ rules: { required: false } }}
							/>

							<VehicleAutoCompleteComponent
								hasClearValue={hasClear}
								setHasClearValue={setHasClear}
								handleChangeVehicle={(vehicle: VehicleAutoCompleteResponse) => {
									setValue("vehicleManufacturer", vehicle?.guidVehicleManufacturer);
									setValue("vehicleModel", vehicle?.guidVehicleModel || "");
									setValue("vehicleSubModel", vehicle?.guidSubModel || "");
									setValue("vehicle", vehicle?.value);
								}}
								vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
								label={getTranslateByKey("information.vehicle")}
							/>

							<XnuupPartIdAutoCompleteComponent
								hasClearValue={hasClear}
								setHasClearValue={setHasClear}
								watcherCategory={null}
								watcherSubcategory={null}
								partPIESName={""}
								handleChangeXnuupPartId={(partId: XnuupPartIdAutoCompleteResponse) => {
									loadOptionsByXnuupPartId(partId);
								}}
								required={false}
								label={getTranslateByKey("information.part")}
							/>

							<KogAutoCompleteControlled
								id="id-partsCatalog-autoComplete-position"
								nameCustom="position"
								label={getTranslateByKey("information.position")}
								options={positionReducer.position.data}
								isLoading={positionReducer.position.isLoading}
								rulesCustom={{ required: false }}
								setValueCustom={setValue}
								disabled={countryReducer.countriesUser.data === null || partWatcher === null}
								control={control}
								onChangeCustom={() => {}}
							/>
						</S.AreaForm>

						<S.AreaButton>
							<SecondaryButton id="id-partsCatalog-button-search" type="submit" isLoading={loadingSave}>
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<SecondaryButton id="id-partsCatalog-button-clear" onClick={handleClickClear}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
						</S.AreaButton>
					</form>
				</Box>

				<PartsCatalogGrid
					formFilters={formFilters}
					gridData={gridData}
					setGridData={setGridData}
					setModalDetails={setModalDetails}
					hasShowMore={hasShowMore}
					setHasShowMore={setHasShowMore}
					pageIndex={pageIndex}
					setPageIndex={setPageIndex}
					isLoadingInfiniteScroll={isLoadingInfiniteScroll}
					setIsLoadingInfiniteScroll={setIsLoadingInfiniteScroll}
				/>
			</ContainerScreen>

			<KogModalSideComponent
				open={modalDetails.opanModal}
				closeModal={() => setModalDetails(INITIAL_MODAL_DETAILS_STATE)}
				title={getTranslateByKey("quotation.partDetail")}
				titleModalReducer={getTranslateByKey("quotation.partDetail")}
			>
				{modalDetails.opanModal && (
					<ModalPartsCatalogDetails
						guidPartCodeMaster={modalDetails.guid}
						closeModal={() => setModalDetails(INITIAL_MODAL_DETAILS_STATE)}
					/>
				)}
			</KogModalSideComponent>
		</>
	);
}

export default withQueryparams(PartsCatalogScreen);
